import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const FAQS = [
  {
    q: "What is the highest efficiency solar cell?",
    a: "The highest efficiency solar cell ever recorded achieved over 47% efficiency in a lab setting using multi-junction technology. However, commercially available solar panels typically achieve 20–24% efficiency using monocrystalline silicon cells. For most buyers, the practical focus should be on high-efficiency panels that are available for purchase, not lab records."
  },
  {
    q: "What is the difference between a solar cell and a solar panel?",
    a: "A solar cell is a single photovoltaic unit that converts sunlight to electricity. A solar panel is a module made up of many solar cells wired together. Solar panel efficiency is always somewhat lower than the individual cell efficiency due to wiring, spacing, and frame losses."
  },
  {
    q: "Are high-efficiency solar panels worth it?",
    a: "Yes, especially if you have limited space. High-efficiency panels produce more power per square foot, which matters for RV roofs, small patios, or compact off-grid setups. If space isn't a concern, lower-efficiency panels may offer better value per watt."
  },
  {
    q: "What type of solar panel is most efficient?",
    a: "Monocrystalline solar panels are the most efficient type available to consumers, typically achieving 20–24% efficiency. Within that category, newer technologies like PERC, HJT, TOPCon, and IBC cells push efficiency even higher."
  },
  {
    q: "Are monocrystalline solar panels better?",
    a: "For most applications, yes. Monocrystalline panels offer higher efficiency, better performance in low light, a longer lifespan, and more power per square foot than polycrystalline or thin-film alternatives."
  },
  {
    q: "Are bifacial solar panels more efficient?",
    a: "Bifacial panels can produce 10–30% more energy than standard panels by capturing reflected light on their back side. They work best over reflective surfaces like white roofs, snow, or light-colored ground."
  },
  {
    q: "Do flexible solar panels produce less power?",
    a: "Generally, yes. Flexible solar panels typically have lower efficiency than rigid panels and may degrade faster due to heat buildup. However, they are valuable for curved surfaces, lightweight applications, and portable setups."
  },
  {
    q: "What size solar panel do I need?",
    a: "It depends on your daily energy consumption, available sun hours, and system losses. Use our Solar Panel Calculator to get a personalized recommendation based on your specific needs."
  },
  {
    q: "How many solar panels do I need for an RV?",
    a: "Most RVers need 200–600 watts of solar, depending on their energy use. A basic setup (lights, phone charging, fan) needs about 200W. A full setup (refrigerator, TV, outlets) typically needs 400–600W or more."
  },
  {
    q: "Can a solar panel charge a solar generator?",
    a: "Yes. Most portable solar generators accept solar panel input via MC4 or proprietary connectors. Check your generator's maximum solar input wattage and voltage range before buying panels."
  },
  {
    q: "What affects solar panel efficiency?",
    a: "Temperature, shading, panel angle, dust, wiring quality, and the charge controller type all affect real-world efficiency. Hot temperatures reduce output, while partial shade can significantly decrease performance."
  },
  {
    q: "Should I buy a solar panel kit or individual panels?",
    a: "Kits are great for beginners — they include matched components (panels, controller, wiring, connectors). Individual panels give more flexibility for custom or expanded systems, but require more knowledge to set up properly."
  },
];

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": FAQS.map(({ q, a }) => ({
    "@type": "Question",
    "name": q,
    "acceptedAnswer": { "@type": "Answer", "text": a },
  })),
};

export default function FAQSection() {
  // Inject FAQ schema into head
  const schemaId = "faq-jsonld";
  if (typeof document !== "undefined") {
    let el = document.getElementById(schemaId);
    if (!el) {
      el = document.createElement("script");
      el.id = schemaId;
      el.type = "application/ld+json";
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(FAQ_SCHEMA);
  }

  return (
    <section className="py-16 lg:py-24">
      <div className="max-w-3xl mx-auto px-4">
        <h2 className="font-heading font-bold text-3xl lg:text-4xl mb-3 text-center">
          Frequently Asked Questions
        </h2>
        <p className="text-muted-foreground text-center mb-10">
          Common questions about the highest efficiency solar cells and solar panels.
        </p>

        <Accordion type="single" collapsible className="space-y-3">
          {FAQS.map((faq, i) => (
            <AccordionItem
              key={i}
              value={`faq-${i}`}
              className="bg-card border border-border rounded-xl px-6 data-[state=open]:border-accent/30"
            >
              <AccordionTrigger className="text-left font-heading font-semibold text-base py-4 hover:no-underline hover:text-accent">
                {faq.q}
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground pb-4 leading-relaxed">
                {faq.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}